import React, { useState, useCallback, useMemo, useRef } from 'react';
import { connect } from 'react-redux';

import { styled } from '@mui/material/styles';
import Slider from '@mui/material/Slider';
import { Typography } from '@mui/material';

import { clearSearchInput, setSearchRadius } from '@apps/shared/src/providerSearch/actions';
import { Accessibility, SearchRadius } from '@apps/shared/src/providerSearch/types/providerSearch';
import { getProviders } from '../actions';

import { RootState } from '../../store';

const mapStateToProps = ({ providerSearch, planSelect }: RootState): StateProps => ({
  searchRadius: providerSearch.radius,
  isLoading: providerSearch.isLoadingProviderList,
  selectedZipCode: providerSearch.selectedZipCode,
  selectedProviderType: providerSearch.selectedProviderType,
  selectedProviderSpecialty: providerSearch.selectedProviderSpecialty,
  selectedAccessibilities: providerSearch.selectedAccessibilities,
  planID: planSelect.selectedPlan?.id,
});

type StateProps = {
  searchRadius: SearchRadius;
  isLoading: boolean;
  selectedZipCode: string;
  selectedProviderType: string;
  selectedProviderSpecialty: string;
  selectedAccessibilities: any[];
  planID: any;
};

type ParentProps = {
  sliderValue: number;
  setSliderValue: React.Dispatch<React.SetStateAction<number>>;
};

const mapDispatchToProps = {
  setSearchRadius,
  clearSearchInput,
  getProviders,
};

type DispatchProps = {
  getProviders: (
    planID: string,
    selectedZipCode: string,
    selectedRadius: string,
    searchInput: string,
    limit: number,
    page: number,
    selectedAccessibilities: Accessibility[],
    selectedProviderType?: string,
    selectedProviderSpecialty?: string
  ) => void;
};

type Props = StateProps & ParentProps & DispatchProps & typeof mapDispatchToProps;
const StyledSlider = styled(Slider)({
  color: '#366BED',
  position: 'relative',
  height: 8,
  padding: '13px 0',
  '& .MuiSlider-thumb': {
    height: 20,
    width: 20,
    backgroundColor: '#366BED',
    border: 'none',
    marginLeft: -12,
    boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
    '&:focus, &:hover, &.Mui-active': {
      boxShadow: '0 2px 8px rgba(54, 107, 237, 0.4)',
    },
    position: 'relative',
    zIndex: 1000,
  },
  '& .MuiSlider-track': {
    height: 2,
    borderRadius: 0,
    background:
      'repeating-linear-gradient(90deg, #366BED 0px, #366BED 8px, transparent 8px, transparent 12px)',
    border: 'none',
  },
  '& .MuiSlider-rail': {
    height: 2,
    borderRadius: 0,
    background:
      'repeating-linear-gradient(90deg, #CCCCCC 0px, #CCCCCC 8px, transparent 8px, transparent 12px)',
    opacity: 1,
    border: 'none',
  },
  '& .MuiSlider-valueLabel': {
    left: 'calc(-50% + 12px)',
    top: 40,
    '& span': {
      transform: 'none',
    },
  },
});

const SliderRoot = styled('div')({
  width: '90%',
  position: 'relative',
  marginTop: '1rem',
});

const Title = styled(Typography)({
  fontSize: '0.875rem',
  fontWeight: 600,
  color: '#000',
  marginBottom: '1rem',
});

const ValueLabel = styled('div')<{ left: string }>(({ left }) => ({
  position: 'absolute',
  top: '-.9rem',
  right: '10.1rem',
  left,
  color: '#366BED',
  fontSize: '0.875rem',
  fontWeight: 600,
  backgroundColor: 'transparent',
  whiteSpace: 'nowrap',
  transform: 'translateX(-50%)',
  zIndex: 999,
}));

const SideLabel = styled(Typography)({
  position: 'absolute',
  color: '#666',
  fontSize: '0.875rem',
});

const LeftLabel = styled(SideLabel)({
  left: 0,
  top: '1.5rem',
});

const RightLabel = styled(SideLabel)({
  right: 0,
  top: '1.5rem',
});

const SliderSelector = ({
  sliderValue,
  isLoading,
  setSliderValue,
  setSearchRadius,
  clearSearchInput,
  getProviders,
  planID,
  selectedZipCode,
  selectedProviderType,
  selectedProviderSpecialty,
  selectedAccessibilities,
}: Props) => {
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const handleChange = useCallback(
    (_: Event, newValue: number | number[]) => {
      const value = Array.isArray(newValue) ? newValue[0] : newValue;
      setSliderValue(value);

      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      debounceTimer.current = setTimeout(() => {
        clearSearchInput();
        const radius = Math.round(value).toString();
        setSearchRadius(radius as SearchRadius);
        getProviders(
          planID,
          selectedZipCode,
          radius,
          '',
          50,
          1,
          selectedAccessibilities,
          selectedProviderType,
          selectedProviderSpecialty
        );
      }, 500);
    },
    [
      selectedZipCode,
      setSearchRadius,
      getProviders,
      planID,
      selectedAccessibilities,
      selectedProviderType,
      selectedProviderSpecialty,
    ]
  );

  return (
    <SliderRoot>
      <Title>Radius</Title>
      <div style={{ position: 'relative' }}>
        <ValueLabel left={`${sliderValue}%`}>{Math.round(sliderValue)}</ValueLabel>
        <StyledSlider
          value={sliderValue}
          onChange={handleChange}
          step={1}
          min={0}
          max={100}
          disabled={isLoading}
        />
        <LeftLabel>0</LeftLabel>
        <RightLabel>100</RightLabel>
      </div>
    </SliderRoot>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(SliderSelector);
